/**
 * What one writer did in the window, from its own hourly counters: one collection per writer, so every number here is
 * that writer's only. Which counters a page shows, and what they are called, is the page's choice
 * (`WriterActivityLayout`); the counters themselves are the writer's names.
 *
 * `WriterActivityView` renders counters it is handed, so any pipeline whose writers count with the shared hourly engine
 * (the load writers' `loadpipe_<source>_hourly`, the broker writers' `brokerpipe_<writer>_activity`) shows them the same
 * way; the default export fetches a load writer's.
 */

import Alert from '@mui/joy/Alert'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import type { LoadPipelineWriterActivityResponse } from '@numeo/types-core'
import { loadPipelineApi } from 'api/loadPipeline'
import { ErrorState, Loading, Stat, StatRow, formatNumber, useLoadPipelineData } from './shared'

type Tone = 'neutral' | 'warning' | 'danger' | 'success'

export interface ActivityStat {
    /** The writer's counter name, e.g. `published`. */
    counter: string
    label: string
    hint: string
    /** The tone for a non-zero value. Zero is always neutral. */
    tone?: Tone
}

export interface WriterActivityLayout {
    /** What "last event" means for this writer: "Last batch", "Last poll". */
    lastEventLabel: string
    /** Stat rows, top to bottom. */
    rows: ActivityStat[][]
    /** Shown when the window has no hour at all. */
    quietNote: string
    /** Show how places were identified and the received-to-published lag under the cards. Defaults to true. */
    showPlacesAndLag?: boolean
    /** For a writer of many boards: the counters of its per-board table, left to right. */
    boardColumns?: { counter: string; label: string }[]
}

/** "3 min ago", "2 h ago": how long the writer has been quiet. */
const quietFor = (lastEventAt: string | null): string => {
    if (!lastEventAt) return 'nothing in this window'
    const minutes = Math.max(0, Math.round((Date.now() - Date.parse(lastEventAt)) / 60_000))
    return minutes < 90 ? `${minutes} min ago` : `${Math.round(minutes / 60)} h ago`
}

const percent = (share: number): string => `${(share * 100).toFixed(share > 0 && share < 0.1 ? 1 : 0)}%`
const duration = (seconds: number): string => (seconds < 90 ? `${Math.round(seconds)} s` : `${(seconds / 60).toFixed(1)} min`)

export default function WriterActivity({ source, hours, layout, board }: { source: string; hours: number; layout: WriterActivityLayout; board?: string }) {
    const { data, error, loading } = useLoadPipelineData(() => loadPipelineApi.getWriterActivity(source, hours, board), [source, hours, board])
    if (error) return <ErrorState error={error} />
    if (loading && !data) return <Loading />
    if (!data) return null
    return <WriterActivityView data={data} layout={layout} board={board} />
}

export function WriterActivityView({ data, layout, board }: { data: LoadPipelineWriterActivityResponse; layout: WriterActivityLayout; board?: string }) {
    const { counters, placeMethods, lastEventAt } = data.totals
    const value = (counter: string): number => counters[counter] ?? 0
    const lagCount = value('lagCount')
    const places = Object.values(placeMethods).reduce((sum, n) => sum + n, 0)
    // Busiest board first. The table lists every board with numbers, whichever board the cards are narrowed to.
    const boards = Object.entries(data.byBoard ?? {}).sort(([, a], [, b]) => (b.counters.received ?? 0) - (a.counters.received ?? 0))

    return (
        <>
            <StatRow>
                <Stat
                    label={layout.lastEventLabel}
                    value={quietFor(lastEventAt)}
                    hint="from this writer's own counters"
                    tone={lastEventAt ? 'neutral' : 'warning'}
                />
                {layout.rows[0]?.map((stat) => (
                    <Stat
                        key={stat.counter}
                        label={stat.label}
                        value={formatNumber(value(stat.counter))}
                        hint={stat.hint}
                        tone={value(stat.counter) > 0 ? stat.tone : 'neutral'}
                    />
                ))}
            </StatRow>
            {layout.rows.slice(1).map((row, index) => (
                <StatRow key={index}>
                    {row.map((stat) => (
                        <Stat
                            key={stat.counter}
                            label={stat.label}
                            value={formatNumber(value(stat.counter))}
                            hint={stat.hint}
                            tone={value(stat.counter) > 0 ? stat.tone : 'neutral'}
                        />
                    ))}
                </StatRow>
            ))}
            {layout.showPlacesAndLag !== false && (lagCount > 0 || places > 0) ? (
                <StatRow>
                    {lagCount > 0 ? (
                        <Stat
                            label="Received to published"
                            value={duration(value('lagSumSeconds') / lagCount)}
                            hint={`average; ${percent(value('lagOver5m') / lagCount)} took over 5 min`}
                            tone={value('lagOver5m') / lagCount > 0.1 ? 'warning' : 'neutral'}
                        />
                    ) : null}
                    {places > 0 ? (
                        <Stat
                            label="Places identified by name"
                            value={percent((placeMethods.EXACT ?? 0) / places)}
                            hint={`the rest by ZIP (${formatNumber(placeMethods.POSTAL ?? 0)}) or point (${formatNumber(placeMethods.GEO ?? 0)})`}
                        />
                    ) : null}
                </StatRow>
            ) : null}

            {data.hours.length === 0 ? (
                <Alert color="warning" variant="soft" sx={{ mb: 3 }}>
                    {layout.quietNote}
                </Alert>
            ) : null}

            {layout.boardColumns && boards.length > 0 ? (
                <Sheet variant="outlined" sx={{ borderRadius: 'sm', p: 2, mb: 3 }}>
                    <Typography level="title-md" sx={{ mb: 1 }}>
                        By board
                    </Typography>
                    <Table size="sm">
                        <thead>
                            <tr>
                                <th>Board</th>
                                {layout.boardColumns.map((column) => (
                                    <th key={column.counter} style={{ textAlign: 'right' }}>
                                        {column.label}
                                    </th>
                                ))}
                                <th style={{ textAlign: 'right' }}>{layout.lastEventLabel}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {boards.map(([name, totals]) => (
                                <tr key={name}>
                                    <td>
                                        <Typography level="body-sm" fontWeight={name === board ? 'lg' : undefined}>
                                            {name}
                                        </Typography>
                                    </td>
                                    {layout.boardColumns?.map((column) => (
                                        <td key={column.counter} style={{ textAlign: 'right' }}>
                                            {formatNumber(totals.counters[column.counter] ?? 0)}
                                        </td>
                                    ))}
                                    <td style={{ textAlign: 'right' }}>{quietFor(totals.lastEventAt)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Sheet>
            ) : null}
        </>
    )
}
